import React, { useEffect, useState, useMemo } from 'react';

import { formatMoney } from 'accounting';
import sanitize from 'utils/sanitize';
import { buildConfiguredURL } from 'utils/routing';

import { ROUTE_MAP } from 'utils/globals';
import { useAppDispatch, useAppSelector } from 'utils/hooks';

import Button from 'components/shared/Button';
import Icon from 'components/shared/Icon';
import Spinner from 'components/shared/Spinner';

import activeEditor from 'domains/active_editor';
import canvases from 'domains/canvases';
import catalogModule from 'domains/catalogs';
import productsModule from 'domains/products';
import navigation from 'domains/navigation';

import {
  selectCatalogsLoaded,
  selectCatalogsLoading,
  selectCustomPriceLoading,
  selectOptionsLoaded,
  selectOptionsLoading,
  selectSubcatalogsLoaded,
  selectSubcatalogsLoading,
} from 'selectors/loading_states';
import { selectPriceTotal, selectProductPrice } from 'selectors/prices';

import PriceToolCustomSizePickerContainer from './PriceToolCustomSizePickerContainer';
import KeepBrowsingLink from './KeepBrowsingLink';
import PriceToolOptionSelect from './PriceToolOptionSelect';

import type { ProductTemplateAPIData } from 'types/catalog_types';
import type { Dimension } from 'types/common_types';

export const DEFAULT_REFERRER = 'https://bayphoto.com/products';

function PriceTool() {
  const dispatch = useAppDispatch();
  const catalogs = useAppSelector(catalogModule.selectors.selectAll);
  const catalogsLoaded = useAppSelector(selectCatalogsLoaded);
  const catalogsLoading = useAppSelector(selectCatalogsLoading);
  const subcatalogs = useAppSelector(activeEditor.selectors.subcatalogs);
  const subcatalogsLoaded = useAppSelector(selectSubcatalogsLoaded);
  const subcatalogsLoading = useAppSelector(selectSubcatalogsLoading);
  const activeCatalog = useAppSelector(activeEditor.selectors.catalog);
  const activeCanvas = useAppSelector(activeEditor.selectors.canvas);
  const activeProduct = useAppSelector(activeEditor.selectors.product);
  const activeCanvasCustomSizes = useAppSelector(activeEditor.selectors.customSize);
  const subcatalogId = useAppSelector(activeEditor.selectors.subcatalogId);
  const products = useAppSelector(activeEditor.selectors.products);
  const optionGroups = useAppSelector(activeEditor.selectors.optionGroups);
  const optionsLoading = useAppSelector(selectOptionsLoading);
  const optionsLoaded = useAppSelector(selectOptionsLoaded);
  const productPrice = useAppSelector(selectProductPrice);
  const total = useAppSelector(selectPriceTotal);
  const customPriceLoading = useAppSelector(selectCustomPriceLoading);
  const canvasConfigPath = useAppSelector(navigation.selectors.canvasConfigPath);
  const canvasOptionsParam = useAppSelector(navigation.selectors.optionsQueryParam);
  const customSizeParam = useAppSelector(navigation.selectors.customSizeQueryParam);

  const [referrer, setReferrer] = useState(DEFAULT_REFERRER);
  const [copyNote, setCopyNote] = useState(false);

  // We need to keep track of the fields for height/width locally so the redux config does not have custom
  // size data for a standard product
  const [priceToolDimension, setPriceToolDimension] = useState<Dimension>({ height: 0, width: 0 });
  const [initialized, setInitialized] = useState(false);

  const customProduct = useMemo(() => {
    return products.find((p) => productsModule.utils.isCustomSizeProduct(p));
  }, [products]);

  const showPriceSpinner = useMemo(() => {
    return catalogsLoading || subcatalogsLoading || optionsLoading;
  }, [catalogsLoading, subcatalogsLoading, optionsLoading]);

  const showPrice = useMemo(() => {
    return catalogsLoaded && subcatalogsLoaded && optionsLoaded;
  }, [catalogsLoaded, optionsLoaded, subcatalogsLoaded]);

  const handlePriceToolSubmit = (e: React.MouseEvent<HTMLButtonElement> | React.FormEvent) => {
    e.preventDefault();
    dispatch(navigation.actions.navigateTo('editor'));
  };

  function copyLink() {
    const url = buildConfiguredURL(
      new URL(window.location.href),
      ROUTE_MAP.editor,
      canvasConfigPath,
      canvasOptionsParam,
      customSizeParam
    );
    const params = new URLSearchParams(url.search);
    params.set('src', 'pricetool');
    url.search = params.toString();

    window.navigator.clipboard.writeText(url.toString()).then(() => {
      setCopyNote(true);
      setTimeout(() => {
        setCopyNote(false);
      }, 2000);
    });
  }

  // run once on mount
  useEffect(() => {
    // referrer?
    const params = new URL(document.location.href).searchParams;
    const rParam = params.get('r');
    if (rParam) {
      setReferrer(rParam);
    }
  }, []);

  useEffect(() => {
    if (catalogs && catalogs.length && !activeCatalog) {
      dispatch(canvases.actions.changeActiveCanvasConfig.catalog(catalogs[0].id));
    }
  }, [catalogs, activeCatalog, dispatch]);

  // Initialize the local size state if it isn't already configured
  useEffect(() => {
    if (!initialized && activeProduct) {
      const hasExistingSize = activeCanvasCustomSizes.height && activeCanvasCustomSizes.width;
      const realDim = productsModule.utils.getProductRealDimension(activeProduct);

      const initialHeight = hasExistingSize ? activeCanvasCustomSizes.height : realDim.height;
      const initialWidth = hasExistingSize ? activeCanvasCustomSizes.width : realDim.width;

      setPriceToolDimension({ height: initialHeight, width: initialWidth });
      setInitialized(true);
    }
  }, [initialized, activeProduct, activeCanvasCustomSizes, priceToolDimension]);

  function handleSelectCatalog(value: string) {
    dispatch(canvases.actions.changeActiveCanvasConfig.catalog(parseInt(value)));
  }

  function handleSelectSubcatalog(value: string) {
    dispatch(canvases.actions.changeActiveCanvasConfig.subcatalog(parseInt(value)));
  }

  function handleSelectProduct(value: string) {
    dispatch(canvases.actions.changeActiveCanvasConfig.product(parseInt(value)));
  }

  function productText(product: ProductTemplateAPIData, products: ProductTemplateAPIData[]) {
    if (products.filter((p) => p.id !== product.id).find((p) => p.name === product.name)) {
      return `${product.name} - (${product.filter_group})`;
    }
    return product.name;
  }

  return (
    <div className="PriceToolWrapper">
      {referrer !== DEFAULT_REFERRER && (
        <a href={`${referrer}?src=pricetool`} className="PriceToolWrapper__close-button">
          <Icon icon="close" size="2x" />
        </a>
      )}
      <div className="PriceTool">
        <form className="PriceTool__form form" onSubmit={handlePriceToolSubmit}>
          <div className="PriceTool__field-set">
            <h2 className="PriceTool__legend">
              <span>Product</span>
            </h2>

            <div className="PriceTool__input">
              <label htmlFor="catalog-select">Catalog</label>
              <select
                id="catalog-select"
                value={activeCanvas && activeCanvas.catalog ? activeCanvas.catalog : ''}
                disabled={!catalogsLoaded}
                onChange={(e) => handleSelectCatalog(e.target.value)}
              >
                {(catalogs || []).map((catalog) => {
                  return (
                    <option key={catalog.id} value={catalog.id}>
                      {catalog.name}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="PriceTool__input">
              <label htmlFor="subcatalog-select">Product</label>
              <select
                id="subcatalog-select"
                value={subcatalogId === null ? '' : subcatalogId}
                disabled={!catalogsLoaded || !subcatalogsLoaded}
                onChange={(e) => handleSelectSubcatalog(e.target.value)}
              >
                {subcatalogs.map((subcatalog) => {
                  return (
                    <option
                      key={subcatalog.id}
                      value={subcatalog.id}
                      disabled={subcatalog.read_only}
                    >
                      {sanitize(subcatalog.name)}
                    </option>
                  );
                })}
              </select>
            </div>

            <label htmlFor="product-select">
              <span>Size</span>
              <span
                className="PriceTool__input__price"
                style={customPriceLoading ? { opacity: '0.25' } : {}}
              >
                {productPrice > 0 && formatMoney(productPrice)}
              </span>
            </label>
            <div className="PriceTool__input-row">
              {customProduct && (
                <PriceToolCustomSizePickerContainer
                  customProduct={customProduct}
                  priceToolDimension={priceToolDimension}
                  setPriceToolDimension={setPriceToolDimension}
                />
              )}
              {!customProduct && (
                <div className="PriceTool__input">
                  <select
                    id="product-select"
                    value={activeProduct?.id === null ? '' : activeProduct?.id}
                    disabled={!catalogsLoaded || !subcatalogsLoaded}
                    onChange={(e) => handleSelectProduct(e.target.value)}
                  >
                    {products.map((product) => {
                      return (
                        <option key={product.id} value={product.id} disabled={product.read_only}>
                          {sanitize(productText(product, products))}
                          {product.id !== activeProduct?.id &&
                            parseFloat(product.price) > 0 &&
                            ` - ${formatMoney(product.price)}`}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}
            </div>
          </div>

          <div className="PriceTool__field-set">
            <h2 className="PriceTool__legend">
              <span>Options</span>
            </h2>
            {optionGroups.map((optionGroup) => (
              <PriceToolOptionSelect key={optionGroup.id} optionGroup={optionGroup} />
            ))}
          </div>

          <input type="submit" value="Submit" style={{ display: 'none' }} />
        </form>
        <div className="PriceTool__results">
          <div className="PriceTool__results__price-wrap">
            {showPriceSpinner && (
              <Spinner
                style={{
                  height: '100%',
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                size="44"
                color="#00a7e1"
                strokeWidth="1"
              />
            )}
            {showPrice && (
              <div
                className="PriceTool__results__price"
                style={customPriceLoading ? { opacity: '0.125' } : {}}
                data-testid="pricetool-price"
              >
                <span>$</span>
                {formatMoney(total, { symbol: '' })}
              </div>
            )}
          </div>
          <div className="PriceTool__actions">
            <Button
              className="OrderNowLink yield"
              disabled={!catalogsLoaded || !subcatalogsLoaded || !optionsLoaded}
              onClick={handlePriceToolSubmit}
            >
              Order Online Now
            </Button>
            <Button type="link" className="copy-link" onClick={copyLink}>
              {copyNote && 'Copied!'}
              {!copyNote && 'Copy Link'}
            </Button>
            <div className="PriceTool__sub-actions">
              Also available in{' '}
              <a href="https://bayphoto.com/order/bay-roes?src=pricetool" className="link regular">
                BayRoes
              </a>{' '}
              or{' '}
              <KeepBrowsingLink
                className="link regular"
                title="keep browsing"
                linkTo={`${referrer}?src=pricetool`}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PriceTool;
